import { useIsCompactViewport } from 'designSystem/hooks/useCompactViewport'
import { type DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { useDocumentCardPagination } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentCardPagination'
import {
  type DrawingRevisionGroup,
  type DrawingRevisionOption,
  type Highlight,
  type PageClassification,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { DocumentResultCard } from './DocumentResultCard'
import {
  buildDocumentResultCardItems,
  resolveDocumentResultCardDisplay,
} from './documentResultCardItems'

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.625rem;
  overflow-y: auto;
  padding-block: 0.625rem;
  padding-inline: 0.625rem;
`

interface DocumentResultsListProps {
  filteredDocuments: DocumentTableRow[]
  totalCount: number
  filteredCount: number
  hasActiveFilters: boolean
  activeIndex: number
  onSelect: (doc: DocumentTableRow) => void
  onSelectPage: (
    doc: DocumentTableRow,
    pageNum: number,
    fragmentText: string
  ) => void
  onOpenInNewTab: (doc: DocumentTableRow, pageNum?: number) => void
  onActiveIndexChange: (index: number) => void
  onActivePreviewDocumentChange: (doc: DocumentTableRow | null) => void
  onActivePreviewPageChange: (page: number) => void
  selectedCardKey: string | null
  onSelectedCardKeyChange: (key: string | null) => void
  selectedRevisionByCardKey: Map<string, DrawingRevisionOption>
  onSelectedRevisionByCardKeyChange: React.Dispatch<
    React.SetStateAction<Map<string, DrawingRevisionOption>>
  >
  highlightsMap: Map<string, Highlight[]>
  pageClassificationsMap: Map<string, PageClassification[]>
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
  documentRowsById: Map<string, DocumentTableRow>
  enablePageCardSurfacing: boolean
  disableClientPaging?: boolean
}

export const DocumentResultsList: React.FC<DocumentResultsListProps> = ({
  filteredDocuments,
  totalCount,
  filteredCount,
  hasActiveFilters,
  activeIndex,
  onSelect,
  onSelectPage,
  onOpenInNewTab,
  onActiveIndexChange,
  onActivePreviewDocumentChange,
  onActivePreviewPageChange,
  selectedCardKey,
  onSelectedCardKeyChange,
  selectedRevisionByCardKey,
  onSelectedRevisionByCardKeyChange,
  highlightsMap,
  pageClassificationsMap,
  drawingRevisionGroupsMap,
  documentRowsById,
  enablePageCardSurfacing,
  disableClientPaging = false,
}) => {
  const cardRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const isCompact = useIsCompactViewport()

  const clientPage = useDocumentCardPagination({
    filteredDocuments,
    totalCount,
    filteredCount,
    hasActiveFilters,
    activeIndex,
    onActiveIndexChange,
  })
  const pageDocuments = disableClientPaging
    ? filteredDocuments
    : clientPage.pageDocuments
  const pageActiveIndex = disableClientPaging
    ? activeIndex
    : clientPage.pageActiveIndex
  const pageStart = disableClientPaging ? 0 : clientPage.pageStart

  const pageCardItems = useMemo(
    () =>
      buildDocumentResultCardItems({
        documents: pageDocuments,
        highlightsMap,
        pageClassificationsMap,
        drawingRevisionGroupsMap,
        documentRowsById,
        enablePageCardSurfacing,
      }),
    [
      pageDocuments,
      highlightsMap,
      pageClassificationsMap,
      drawingRevisionGroupsMap,
      documentRowsById,
      enablePageCardSurfacing,
    ]
  )
  const activeDocumentId = pageDocuments[pageActiveIndex]?.id
  const selectedCardStillActive =
    selectedCardKey &&
    pageCardItems.some(
      (item) =>
        item.key === selectedCardKey &&
        itemContainsDocumentId(item, activeDocumentId)
    )
  const activeCardKey = selectedCardStillActive
    ? selectedCardKey
    : pageCardItems.find((item) =>
        itemContainsDocumentId(item, activeDocumentId)
      )?.key
  const activePreviewTarget = useMemo(() => {
    const activeItem = pageCardItems.find((item) => item.key === activeCardKey)
    if (!activeItem || activeItem.kind === 'document') {
      return { document: null, pageNumber: 1 }
    }
    const display = resolveDocumentResultCardDisplay({
      documentRowsById,
      item: activeItem,
      selectedRevision: selectedRevisionByCardKey.get(activeItem.key),
    })
    return {
      document: display.document,
      pageNumber: display.pageNumber,
    }
  }, [
    activeCardKey,
    documentRowsById,
    pageCardItems,
    selectedRevisionByCardKey,
  ])

  useEffect(() => {
    const activeCardIndex = pageCardItems.findIndex(
      (item) => item.key === activeCardKey
    )
    if (activeCardIndex < 0) return
    const el = cardRefs.current.get(activeCardIndex)
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' })
    })
  }, [activeCardKey, pageCardItems])

  useEffect(() => {
    onActivePreviewDocumentChange(activePreviewTarget.document)
    onActivePreviewPageChange(activePreviewTarget.pageNumber)
  }, [
    activePreviewTarget.document,
    activePreviewTarget.pageNumber,
    onActivePreviewDocumentChange,
    onActivePreviewPageChange,
  ])

  return (
    <List>
      <CardList>
        {pageCardItems.map((item, pageIdx) => {
          const pageDocumentIndex = resolvePageDocumentIndex(
            pageDocuments,
            item
          )
          const globalIdx =
            pageDocumentIndex >= 0 ? pageStart + pageDocumentIndex : null
          const firstFragment = item.highlights?.[0]?.fragments[0] ?? ''
          const display = resolveDocumentResultCardDisplay({
            documentRowsById,
            item,
            selectedRevision: selectedRevisionByCardKey.get(item.key),
          })

          return (
            <DocumentResultCard
              key={item.key}
              document={display.document}
              isCompact={isCompact}
              onClick={() => {
                onSelectedCardKeyChange(item.key)
                if (globalIdx !== null) onActiveIndexChange(globalIdx)
                onActivePreviewDocumentChange(
                  display.isPageCard ? display.document : null
                )
                onActivePreviewPageChange(
                  display.isPageCard ? display.pageNumber : 1
                )
              }}
              onDoubleClick={() => {
                if (display.isPageCard) {
                  onSelectPage(
                    display.document,
                    display.pageNumber,
                    firstFragment
                  )
                  return
                }
                onSelect(display.document)
              }}
              onOpenInNewTab={() =>
                onOpenInNewTab(
                  display.document,
                  display.isPageCard ? display.pageNumber : undefined
                )
              }
              cardRef={(el) => {
                if (el) cardRefs.current.set(pageIdx, el)
                else cardRefs.current.delete(pageIdx)
              }}
              pageNumber={display.pageNumber}
              thumbnailBqDocumentId={display.thumbnailBqDocumentId}
              isPageCard={display.isPageCard}
              drawingNumber={display.drawingNumber}
              sheetNumber={display.sheetNumber}
              revisionCode={display.revisionCode}
              revisionStackingFailed={display.revisionStackingFailed}
              revisionOptions={display.revisionOptions}
              selectedRevisionKey={display.selectedRevisionKey}
              onRevisionChange={(option) => {
                const revisionDisplay = resolveDocumentResultCardDisplay({
                  documentRowsById,
                  item,
                  selectedRevision: option,
                })
                onSelectedRevisionByCardKeyChange((current) => {
                  const next = new Map(current)
                  next.set(item.key, option)
                  return next
                })
                onSelectedCardKeyChange(item.key)
                if (globalIdx !== null) onActiveIndexChange(globalIdx)
                onActivePreviewDocumentChange(revisionDisplay.document)
                onActivePreviewPageChange(revisionDisplay.pageNumber)
              }}
            />
          )
        })}
      </CardList>
    </List>
  )
}

function resolvePageDocumentIndex(
  pageDocuments: DocumentTableRow[],
  item: ReturnType<typeof buildDocumentResultCardItems>[number]
): number {
  const candidateIds =
    item.kind === 'drawingPage'
      ? [
          item.sourceDocumentId,
          item.document.id,
          ...(item.revisionOptions?.map((option) => option.document_id) ?? []),
        ]
      : [item.document.id]

  for (const candidateId of candidateIds) {
    const index = pageDocuments.findIndex((doc) => doc.id === candidateId)
    if (index >= 0) return index
  }

  return -1
}

function itemContainsDocumentId(
  item: ReturnType<typeof buildDocumentResultCardItems>[number],
  documentId: string | undefined
): boolean {
  if (!documentId) return false
  if (item.document.id === documentId) return true
  if (item.kind !== 'drawingPage') return false
  if (item.sourceDocumentId === documentId) return true
  return (
    item.revisionOptions?.some((option) => option.document_id === documentId) ??
    false
  )
}
